import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getMasterFileSummary, getMasterFileDuplicates, getMasterFileExport } from "../lib/api";
import { logEvent } from "../lib/log";
import DownloadModal from "../components/DownloadModal";

function fmtDomainSource(v) {
  if (!v) return "";
  const map = {
    INPUT_DOMAIN_RAW: "Input Domain (Verified)",
    SONNET_KNOWLEDGE: "LLM2",
    WEB_SEARCH:       "LLM Search",
    NOT_FOUND:        "",
    ADDR_ONLY:        "",
  };
  if (v in map) return map[v];
  if (/haiku|step[\s_]?1/i.test(v)) return "LLM1";
  if (/sonnet|step[\s_]?2/i.test(v)) return "LLM2";
  if (/step[\s_]?[34]/i.test(v))     return "LLM Search";
  return "LLM";
}

function downloadMasterCSV(rows, assignments) {
  const esc = (v) => {
    if (v == null) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const parseList = (v) => {
    if (!v) return "";
    try {
      const arr = typeof v === "string" ? JSON.parse(v) : v;
      return Array.isArray(arr) ? arr.join("; ") : String(v);
    } catch { return String(v); }
  };
  const digitsOnly = (v) => v ? String(v).replace(/\D/g, "") : "";

  const headers = [
    // SOURCE (raw import data)
    "System Name",
    "Vendor / Customer #",
    "Vendor Name",
    "Address Seq",
    // AI ENRICHMENT
    "AI Vendor Name",
    "AI Domain",
    "AI Domain Confidence %",
    "AI Domain Source",
    "AI NAICS Code",
    "AI NAICS Title",
    "AI NAICS Confidence %",
    "AI Business Description",
    "AI Key Offerings",
    "AI Industries Served",
    "AI Profile Confidence %",
    "AI Is Person",
    "AI Is Intercompany",
    "AI Address Line 1",
    "AI Address Line 2",
    "AI Address Line 3",
    "AI Address Line 4",
    "AI PO Box",
    "AI City",
    "AI State / Province",
    "AI ZIP / Postal Code",
    "AI Country",
    "AI Phone Formatted",
    "AI Phone E.164",
    "AI Phone Digits Only",
    "AI Email",
    // Meta
    "Last Enriched",
    "Dup Domain (Same System)",
    "Dup Domain (Cross System)",
    "Dup Address",
  ];

  // Build a map of master_record_id → best company-level data so secondary
  // address seqs always show vendor-level enrichment fields.
  const vendorData = new Map();
  for (const r of rows) {
    const id = r.master_record_id;
    if (!id) continue;
    const existing = vendorData.get(id);
    if (!existing || (r.domain_value && !existing.domain_value) || (r.naics_code && !existing.naics_code)) {
      vendorData.set(id, r);
    }
  }

  const csvRows = rows.map(r => {
    const base = (r.master_record_id && vendorData.get(r.master_record_id)) || r;
    // Dup codes come directly from master_records export (preferred) or assignments fallback
    const dupSame  = base.dup_domain_same  ?? assignments?.[r.master_record_id]?.dup_domain_same  ?? "";
    const dupCross = base.dup_domain_cross ?? assignments?.[r.master_record_id]?.dup_domain_cross ?? "";
    const dupAddr  = base.dup_address      ?? assignments?.[r.master_record_id]?.dup_address      ?? "";
    return [
      r.system_name,
      r.vendor_num,
      r.vendor_name_source,
      r.address_seq ?? 1,
      base.company_name_recommended || base.vendor_name_source,
      base.domain_value,
      base.domain_confidence,
      fmtDomainSource(base.domain_source),
      base.naics_code,
      base.naics_title,
      base.naics_confidence,
      base.what_they_do,
      parseList(base.key_offerings),
      parseList(base.industries_served),
      base.profile_confidence,
      base.is_person   ? "Yes" : "No",
      base.is_internal ? "Yes" : "No",
      r.address_line_1,
      r.address_line_2,
      r.address_line_3,
      r.address_line_4,
      r.po_box_std,
      r.city,
      r.state_province,
      r.zip_postal_code,
      r.country,
      r.phone_formatted,
      r.phone_e164,
      digitsOnly(r.phone_e164 || r.phone_formatted),
      r.email_std,
      r.last_enriched_at ? new Date(r.last_enriched_at).toLocaleDateString("en-US") : "",
      dupSame,
      dupCross,
      dupAddr,
    ].map(esc).join(",");
  });

  const csv  = [headers.join(","), ...csvRows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url; a.download = "master_file_enriched.csv"; a.click();
  URL.revokeObjectURL(url);
}

const BLUE  = "#0176CE";
const GREEN = "#10b981";

const NAICS_SECTORS = {
  '11': 'Agriculture, Forestry & Fishing',
  '21': 'Mining, Quarrying & Oil/Gas',
  '22': 'Utilities',
  '23': 'Construction',
  '31': 'Manufacturing', '32': 'Manufacturing', '33': 'Manufacturing',
  '42': 'Wholesale Trade',
  '44': 'Retail Trade',  '45': 'Retail Trade',
  '48': 'Transportation & Warehousing', '49': 'Transportation & Warehousing',
  '51': 'Information',
  '52': 'Finance & Insurance',
  '53': 'Real Estate & Rental',
  '54': 'Professional & Technical Services',
  '55': 'Management of Companies',
  '56': 'Administrative & Support Services',
  '61': 'Educational Services',
  '62': 'Health Care & Social Assistance',
  '71': 'Arts, Entertainment & Recreation',
  '72': 'Accommodation & Food Services',
  '81': 'Other Services',
  '92': 'Public Administration',
};

const ENRICHMENT_ROWS = [
  { key: 'names',    label: 'Company Names Cleaned',  statKey: 'names_cleaned',     color: '#0176CE', filter: 'names'    },
  { key: 'address',  label: 'Addresses Standardized', statKey: 'addresses_cleaned', color: '#F37428', filter: 'address'  },
  { key: 'domain',   label: 'Websites Found',         statKey: 'domains_found',     color: '#0176CE', filter: 'domain'   },
  { key: 'naics',    label: 'NAICS Codes Assigned',   statKey: 'naics_found',       color: '#10b981', filter: 'naics'    },
  { key: 'profile',  label: 'Business Profiles',      statKey: 'profiles_found',    color: '#F37428', filter: 'profile'  },
  { key: 'person',   label: 'People Identified',      statKey: 'people_found',      color: '#f59e0b', filter: 'person'   },
  { key: 'internal', label: 'Intercompany Flagged',   statKey: 'internal_found',    color: '#8b5cf6', filter: 'internal' },
  { key: 'error',    label: 'No Enrichment Found',    statKey: 'errors',            color: '#ef4444', filter: 'error'    },
];

function fmtDate(raw) {
  if (!raw) return "—";
  return new Date(raw).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function StatCard({ label, value, sub }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statValue}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}

function EnrichmentBar({ label, count, total, color, filter, navigate }) {
  const [hover, setHover] = useState(false);
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const fillWidth = total > 0 ? Math.max(2, (count / total) * 100) : 0;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 0", cursor: "pointer",
        borderBottom: "1px solid #f1f3f8",
        background: hover ? "#fafbff" : "transparent",
        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
        transition: "background 0.1s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(`/master-file/records?filter=${filter}`)}
    >
      <div style={{ width: 190, flexShrink: 0, color: "#374151", fontSize: 13, fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, height: 8, background: "#e9ecf3", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${fillWidth}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ width: 50, textAlign: "right", color: "#374151", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{count.toLocaleString()}</div>
      <div style={{ width: 44, textAlign: "right", color: "#9ca3af", fontSize: 12, flexShrink: 0 }}>{pct}%</div>
      <div style={{ width: 16, color: hover ? "#0176CE" : "transparent", fontSize: 14, flexShrink: 0, textAlign: "right" }}>›</div>
    </div>
  );
}

function NaicsBar({ sector, sectors, count, total, navigate }) {
  const [hover, setHover] = useState(false);
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const fillWidth = total > 0 ? Math.max(2, (count / total) * 100) : 0;
  const isNull = sector === null;
  const label  = isNull ? "Not classified" : (NAICS_SECTORS[sector] || `Sector ${sector}`);
  const color  = isNull ? "#d1d5db" : GREEN;
  const allSectors = sectors || [sector];

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 0", cursor: "pointer",
        borderBottom: "1px solid #f1f3f8",
        background: hover ? "#fafbff" : "transparent",
        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
        transition: "background 0.1s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(isNull
        ? `/master-file/records?filter=unclassified`
        : `/master-file/records?filter=naics_sector&sector=${allSectors.join(',')}`
      )}
    >
      <div style={{ width: 260, flexShrink: 0, color: isNull ? "#9ca3af" : "#374151", fontSize: 13, fontWeight: 500, fontStyle: isNull ? "italic" : "normal" }}>{label}</div>
      <div style={{ flex: 1, height: 8, background: "#e9ecf3", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${fillWidth}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ width: 50, textAlign: "right", color: "#374151", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{count.toLocaleString()}</div>
      <div style={{ width: 44, textAlign: "right", color: "#9ca3af", fontSize: 12, flexShrink: 0 }}>{pct}%</div>
      <div style={{ width: 16, color: hover ? "#0176CE" : "transparent", fontSize: 14, flexShrink: 0, textAlign: "right" }}>›</div>
    </div>
  );
}

function CompletenessBar({ label, hasCount, pct, total, onClick }) {
  const [hover, setHover] = useState(false);
  const missingCount = (total || 0) - (hasCount || 0);
  const missingPct   = total > 0 ? Math.round((missingCount / total) * 100) : 0;
  const fillWidth    = total > 0 ? Math.max(missingCount > 0 ? 2 : 0, (missingCount / total) * 100) : 0;
  const color        = missingPct >= 50 ? "#ef4444" : missingPct >= 20 ? "#f59e0b" : "#10b981";
  const disabled     = missingCount === 0;

  return (
    <div
      style={{
        display: "grid", gridTemplateColumns: "150px 1fr 54px 150px",
        alignItems: "center", gap: 12,
        padding: "10px 0",
        cursor: disabled ? "default" : "pointer",
        borderBottom: "1px solid #f1f3f8",
        background: hover && !disabled ? "#fafbff" : "transparent",
        marginLeft: -28, marginRight: -28, paddingLeft: 28, paddingRight: 28,
        transition: "background 0.1s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={disabled ? undefined : onClick}
    >
      <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{label}</div>
      <div style={{ height: 10, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
        <div style={{ height: "100%", background: color, borderRadius: 99, width: `${fillWidth}%`, transition: "width 0.4s ease" }} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: disabled ? "#9ca3af" : color, textAlign: "right" }}>
        {missingPct}%
      </div>
      <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "right" }}>
        {disabled
          ? <span style={{ color: "#10b981" }}>✓ All filled</span>
          : <>{missingCount.toLocaleString()} missing <span style={{ color: hover ? BLUE : "transparent", fontSize: 14 }}>›</span></>
        }
      </div>
    </div>
  );
}

function GeoRow({ label, cnt, total, onClick }) {
  const [hover, setHover] = useState(false);
  const pct = total > 0 ? Math.round((cnt / total) * 100) : 0;
  return (
    <tr
      style={{ background: hover ? "#fafbff" : "inherit", cursor: "pointer", transition: "background 0.1s" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      <td style={styles.geoTd}>{label}</td>
      <td style={{ ...styles.geoTd, textAlign: "right", fontWeight: 600 }}>{cnt.toLocaleString()}</td>
      <td style={{ ...styles.geoTd, textAlign: "right" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
          <div style={{ height: 6, background: "#f59e0b", borderRadius: 99, width: Math.max(2, pct * 0.8), minWidth: 2 }} />
          <span style={{ fontSize: 12, color: "#6b7280", minWidth: 32, textAlign: "right" }}>{pct}%</span>
          <span style={{ color: hover ? BLUE : "transparent", fontSize: 13 }}>›</span>
        </div>
      </td>
    </tr>
  );
}

const DUP_BADGE = {
  same:  { bg: "rgba(1,118,206,0.1)",   color: "#0176CE", label: "Same system"  },
  cross: { bg: "rgba(239,68,68,0.08)",  color: "#ef4444", label: "Cross system" },
  addr:  { bg: "rgba(245,158,11,0.1)",  color: "#f59e0b", label: "Address match" },
};

function DupRow({ label, count, badge, detail, path, navigate }) {
  const [hover, setHover] = useState(false);
  const b = DUP_BADGE[badge];
  const zero = count === 0;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "11px 0", borderBottom: "1px solid #f1f5f9",
        cursor: zero ? "default" : "pointer",
        background: hover && !zero ? "#fafbff" : "transparent",
        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
        transition: "background 0.1s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={zero ? undefined : () => navigate(path)}
    >
      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: b.bg, color: b.color, flexShrink: 0 }}>{b.label}</span>
      <span style={{ fontSize: 13, color: "#374151", flex: 1 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, color: zero ? "#9ca3af" : "#0f172a", flexShrink: 0 }}>{count === 0 ? "None found" : detail}</span>
      <span style={{ color: hover && !zero ? BLUE : "transparent", fontSize: 14, flexShrink: 0 }}>›</span>
    </div>
  );
}

export default function MasterFile() {
  const navigate = useNavigate();
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [downloading,  setDownloading]  = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const [dupData,      setDupData]      = useState(null);
  const [dupLoading,   setDupLoading]   = useState(false);
  const [dupError,     setDupError]     = useState("");

  async function handleDownload() {
    setDownloading(true);
    try {
      const exportRes   = await getMasterFileExport();
      const assignments = dupData?.assignments || {};
      downloadMasterCSV(exportRes.rows || [], assignments);
      setShowDownload(false);
    } catch (e) {
      alert("Download failed: " + e.message);
    } finally {
      setDownloading(false);
    }
  }

  useEffect(() => {
    logEvent('page_view', { page: 'master_file' });
    getMasterFileSummary()
      .then(setData)
      .catch(e => setError(e.message || "Could not load master file data."))
      .finally(() => setLoading(false));
    setDupLoading(true);
    getMasterFileDuplicates()
      .then(setDupData)
      .catch(e => setDupError(e.message || "Could not load duplicates."))
      .finally(() => setDupLoading(false));
  }, []);

  const overview       = data?.overview       || {};
  const stats          = data?.stats          || {};
  const completeness   = data?.completeness   || {};
  const naicsBreakdown = data?.naics_breakdown || [];
  const countries      = data?.countries      || [];
  const states         = data?.states         || [];
  const systems        = data?.systems        || [];

  const total  = stats.total || 0;
  const qs     = stats.quality_score ?? 0;
  const qsColor = qs >= 80 ? "#10b981" : qs >= 60 ? "#f59e0b" : "#ef4444";

  const compFields = [
    { label: "Domain / Website", key: "domain",  filter: "domain"  },
    { label: "Address",          key: "address", filter: "address" },
    { label: "Country",          key: "country", filter: "address" },
    { label: "Phone",            key: "phone",   filter: "all"     },
    { label: "Email",            key: "email",   filter: "all"     },
    { label: "Tax ID",           key: "taxid",   filter: "all"     },
    { label: "NAICS Code",       key: "naics",   filter: "naics"   },
  ];

  const countryTotal = countries.reduce((s, r) => s + r.cnt, 0);
  const stateTotal   = states.reduce((s, r) => s + r.cnt, 0);

  return (
    <div style={styles.page}>
      {showDownload && (
        <DownloadModal
          onClose={() => setShowDownload(false)}
          onDownload={handleDownload}
          downloading={downloading}
          vendorCount={overview.total_records}
          addressCount={overview.total_addresses}
          includeDups={true}
        />
      )}
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="master-file" />
      <main style={styles.main}>

        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Master File</h1>
            <p style={styles.sub}>Your enriched vendor &amp; customer database</p>
          </div>
          {!loading && overview.total_records > 0 && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button style={styles.downloadBtn} onClick={() => setShowDownload(true)}>
                ↓ Download CSV
              </button>
              <button style={styles.viewAllBtn} onClick={() => navigate("/master-file/records")}>
                View all records →
              </button>
            </div>
          )}
        </div>

        {loading && <div style={styles.empty}>Loading…</div>}
        {error   && <div style={{ color: "#ef4444", padding: "12px 0" }}>{error}</div>}

        {!loading && !error && overview.total_records === 0 && (
          <div style={styles.emptyBox}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
            <div style={{ color: "#111827", fontWeight: 600, marginBottom: 6 }}>No enriched records yet</div>
            <div style={{ color: "#6b7280", fontSize: 14 }}>Upload and process a file to build your master file.</div>
          </div>
        )}

        {!loading && !error && overview.total_records > 0 && (
          <>
            {/* ── Overview cards ── */}
            <div style={styles.statRow}>
              <StatCard
                label="Total Vendors"
                value={overview.total_records.toLocaleString()}
                sub={`${overview.enriched_count.toLocaleString()} enriched`}
              />
              <StatCard
                label="Total Addresses"
                value={(overview.total_addresses || 0).toLocaleString()}
                sub={overview.total_records > 0 ? `${((overview.total_addresses || 0) / overview.total_records).toFixed(1)} avg per vendor` : "—"}
              />
              <StatCard
                label="Source Systems"
                value={overview.system_count}
                sub={systems.map(s => s.system_name).join(", ") || "—"}
              />
              <StatCard
                label="Last Enriched"
                value={fmtDate(overview.last_enriched_at)}
                sub={overview.enriched_count > 0 ? `${Math.round((overview.enriched_count / overview.total_records) * 100)}% coverage` : "—"}
              />
            </div>

            {/* ── Enrichment Results + Quality Score ── */}
            <div style={styles.twoCol}>
              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Enrichment Results</h2>
                <p style={styles.cardSub}>Click any row to drill into those records</p>
                <div style={{ marginTop: 4 }}>
                  {ENRICHMENT_ROWS.map(row => (
                    <EnrichmentBar
                      key={row.key}
                      label={row.label}
                      count={stats[row.statKey] || 0}
                      total={total}
                      color={row.color}
                      filter={row.filter}
                      navigate={navigate}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.card}>
                <h2 style={styles.cardTitle}>Quality Score</h2>
                <div style={styles.qualityScoreBox}>
                  <div style={{ fontSize: 64, fontWeight: 800, color: qsColor, lineHeight: 1, letterSpacing: "-2px" }}>
                    {qs != null ? `${qs}%` : "—"}
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 8 }}>Enrichment coverage score</div>
                </div>
                <div style={styles.divider} />
                <div style={styles.statsList}>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Enrichment Rate</span>
                    <span style={styles.statsValue}>{(() => {
                      const base = (stats.total||0) - (stats.people_found||0) - (stats.internal_found||0);
                      return base > 0 ? `${Math.round(((stats.enriched||0)/base)*100)}%` : "—";
                    })()}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Avg Domain Confidence</span>
                    <span style={styles.statsValue}>{stats.avg_domain_confidence != null ? `${stats.avg_domain_confidence}%` : "—"}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Avg Profile Confidence</span>
                    <span style={styles.statsValue}>{stats.avg_profile_confidence != null ? `${stats.avg_profile_confidence}%` : "—"}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>Vendors Enriched</span>
                    <span style={styles.statsValue}>{(stats.enriched || 0).toLocaleString()} / {((stats.total || 0) - (stats.people_found || 0) - (stats.internal_found || 0)).toLocaleString()}</span>
                  </div>
                  <div style={styles.statsRow}>
                    <span style={styles.statsLabel}>No Enrichment Found</span>
                    <span style={{ ...styles.statsValue, color: stats.errors > 0 ? "#ef4444" : "#9ca3af" }}>
                      {(stats.errors || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── NAICS Industry Breakdown ── */}
            {(naicsBreakdown.length > 0 || overview.people_count > 0) && (
              <div style={{ ...styles.card, marginTop: 24 }}>
                <h2 style={styles.cardTitle}>Industry Breakdown (NAICS)</h2>
                <p style={styles.cardSub}>Click any sector to view those records</p>
                <div style={{ marginTop: 4 }}>
                  {(() => {
                    // Merge rows that share the same sector label (e.g. 31/32/33 → Manufacturing)
                    const merged = [];
                    const seen = new Map(); // label → index in merged
                    for (const item of naicsBreakdown) {
                      const label = item.sector == null ? null : (NAICS_SECTORS[item.sector] || `Sector ${item.sector}`);
                      const key = label ?? '__null__';
                      if (seen.has(key)) {
                        const idx = seen.get(key);
                        merged[idx].count += item.count;
                        merged[idx].sectors.push(item.sector);
                      } else {
                        seen.set(key, merged.length);
                        merged.push({ sector: item.sector, sectors: [item.sector], count: item.count });
                      }
                    }
                    return merged.map(item => (
                      <NaicsBar
                        key={item.sectors.join(',')}
                        sector={item.sector}
                        sectors={item.sectors}
                        count={item.count}
                        total={stats.enriched || total}
                        navigate={navigate}
                      />
                    ));
                  })()}
                  {/* Person row */}
                  {overview.people_count > 0 && (
                    <div
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "10px 0", cursor: "pointer",
                        background: "transparent",
                        marginLeft: -16, marginRight: -16, paddingLeft: 16, paddingRight: 16,
                      }}
                      onClick={() => navigate("/master-file/records?filter=person")}
                    >
                      <div style={{ width: 260, flexShrink: 0, color: "#f59e0b", fontSize: 13, fontWeight: 500, fontStyle: "italic" }}>Flagged as Person</div>
                      <div style={{ flex: 1, height: 8, background: "#e9ecf3", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: `${total > 0 ? Math.max(2, (overview.people_count / total) * 100) : 0}%`, height: "100%", background: "#f59e0b", borderRadius: 4 }} />
                      </div>
                      <div style={{ width: 50, textAlign: "right", color: "#374151", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{overview.people_count}</div>
                      <div style={{ width: 44, textAlign: "right", color: "#9ca3af", fontSize: 12, flexShrink: 0 }}>{total > 0 ? Math.round((overview.people_count / total) * 100) : 0}%</div>
                      <div style={{ width: 16, color: "#0176CE", fontSize: 14, flexShrink: 0, textAlign: "right" }}>›</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Potential Duplicates ── */}
            <div style={{ ...styles.card, marginTop: 24, padding: "24px 28px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                  <h2 style={styles.cardTitle}>Potential Duplicates</h2>
                  <p style={styles.cardSub}>Automatically detected across your master file</p>
                </div>
                {dupLoading && <span style={{ fontSize: 13, color: "#6b7280" }}>Analyzing…</span>}
              </div>
              {dupError && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{dupError}</div>}
              <div style={styles.dupCardRow}>
                {[
                  {
                    label:  "Same Domain (Same System)",
                    count:  dupData?.summary?.domain_same_groups ?? "—",
                    sub:    dupData ? "groups" : "",
                    badge:  "same",
                    path:   "/master-file/duplicates?tab=domain&type=same",
                  },
                  {
                    label:  "Same Domain (Cross System)",
                    count:  dupData?.summary?.domain_cross_groups ?? "—",
                    sub:    dupData ? "groups" : "",
                    badge:  "cross",
                    path:   "/master-file/duplicates?tab=domain&type=cross",
                  },
                  {
                    label:  "Duplicate Addresses",
                    count:  dupData?.summary?.address_groups ?? "—",
                    sub:    dupData ? `${dupData.summary.address_records} records` : "",
                    badge:  "addr",
                    path:   "/master-file/duplicates?tab=address",
                  },
                ].map(({ label, count, sub, badge, path }) => {
                  const b = DUP_BADGE[badge];
                  const zero = count === 0;
                  return (
                    <div
                      key={label}
                      onClick={zero || !dupData ? undefined : () => navigate(path)}
                      style={{
                        flex: 1, border: `1px solid ${zero ? "#e2e8f0" : b.color + "44"}`,
                        borderRadius: 12, padding: "20px 22px",
                        cursor: zero || !dupData ? "default" : "pointer",
                        background: zero ? "#fafafa" : b.bg,
                        transition: "box-shadow 0.15s",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 700, color: b.color, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>{b.label}</div>
                      <div style={{ fontSize: 34, fontWeight: 800, color: zero ? "#9ca3af" : b.color, lineHeight: 1, letterSpacing: "-1px" }}>
                        {dupLoading ? <span style={{ fontSize: 18, color: "#9ca3af" }}>…</span> : count}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{label}</div>
                      {sub && !zero && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
                      {zero && <div style={{ fontSize: 11, color: "#10b981", marginTop: 6, fontWeight: 600 }}>✓ None found</div>}
                    </div>
                  );
                })}
              </div>
              {dupData && (
                <div style={{ marginTop: 14, fontSize: 12, color: "#9ca3af" }}>
                  Dup codes (DOM-xxx, ADR-xxx) are included in the CSV download for sorting in Excel.
                </div>
              )}
            </div>

            {/* ── Data Completeness ── */}
            <div style={{ ...styles.card, marginTop: 24 }}>
              <h2 style={styles.cardTitle}>Missing Data</h2>
              <p style={styles.cardSub}>
                Based on your enriched master file — shows fields still missing after AI enrichment across {(completeness.total || 0).toLocaleString()} business vendors. Excludes persons and intercompany records. Click any field to see vendors missing that data.
              </p>
              <div style={{ marginTop: 4 }}>
                {compFields.map(({ label, key, filter }) => (
                  <CompletenessBar
                    key={key}
                    label={label}
                    hasCount={completeness[key]?.count || 0}
                    total={completeness.total || 0}
                    onClick={() => navigate(`/master-file/records?filter=${filter}&missing=1`)}
                  />
                ))}
              </div>
            </div>

            {/* ── Geography ── */}
            <div style={styles.geoSection}>
              {/* Country */}
              <div style={styles.geoCard}>
                <div style={styles.geoTitle}>By Country</div>
                {countries.length === 0
                  ? <div style={styles.geoEmpty}>No country data available.</div>
                  : (
                    <table style={styles.geoTable}>
                      <thead>
                        <tr>
                          <th style={styles.geoTh}>Country</th>
                          <th style={{ ...styles.geoTh, textAlign: "right" }}>Records</th>
                          <th style={{ ...styles.geoTh, textAlign: "right" }}>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {countries.map(r => (
                          <GeoRow
                            key={r.country}
                            label={r.country}
                            cnt={r.cnt}
                            total={countryTotal}
                            onClick={() => navigate(`/master-file/records?country=${encodeURIComponent(r.country)}`)}
                          />
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>

              {/* US States */}
              <div style={styles.geoCard}>
                <div style={styles.geoTitle}>US States</div>
                {states.length === 0
                  ? <div style={styles.geoEmpty}>No US state data available.</div>
                  : (
                    <table style={styles.geoTable}>
                      <thead>
                        <tr>
                          <th style={styles.geoTh}>State</th>
                          <th style={{ ...styles.geoTh, textAlign: "right" }}>Records</th>
                          <th style={{ ...styles.geoTh, textAlign: "right" }}>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {states.map(r => (
                          <GeoRow
                            key={r.state}
                            label={r.state}
                            cnt={r.cnt}
                            total={stateTotal}
                            onClick={() => navigate(`/master-file/records?country=US&state=${encodeURIComponent(r.state)}`)}
                          />
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </div>
            </div>
          </>
        )}
      </main>
      </div>
    </div>
  );
}

const styles = {
  page:      { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc" },
  topStripe: { height: 4, background: `linear-gradient(90deg, ${BLUE}, #F37428)`, flexShrink: 0 },
  body:      { display: "flex", flex: 1 },
  main:   { flex: 1, padding: "40px 48px", overflowY: "auto" },
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 32 },
  title:  { fontSize: 28, fontWeight: 700, color: "#111827", margin: 0 },
  sub:    { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  empty:  { color: "#9ca3af", padding: "40px 0", textAlign: "center" },
  emptyBox: { textAlign: "center", padding: "60px 40px", background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0" },
  downloadBtn: { background: "#fff", color: "#0176CE", border: "1px solid #0176CE", borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", marginTop: 4 },
  dupCardRow:  { display: "flex", gap: 16 },
  viewAllBtn: { background: "#0176CE", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", marginTop: 4 },

  statRow:  { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 24 },
  statCard: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "24px 28px" },
  statValue:{ fontSize: 30, fontWeight: 700, color: "#0f172a", lineHeight: 1, marginBottom: 6 },
  statLabel:{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 },
  statSub:  { fontSize: 12, color: "#94a3b8" },

  twoCol:   { display: "grid", gridTemplateColumns: "65fr 35fr", gap: 24 },
  card:     { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "24px 16px" },
  cardTitle:{ color: "#111827", fontSize: 16, fontWeight: 700, margin: "0 0 2px", letterSpacing: "-0.3px" },
  cardSub:  { color: "#9ca3af", fontSize: 12, margin: "0 0 4px" },

  qualityScoreBox: { textAlign: "center", padding: "24px 0 20px" },
  divider:  { borderTop: "1px solid #e2e8f0", margin: "16px 0" },
  statsList:{ display: "flex", flexDirection: "column", gap: 12 },
  statsRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  statsLabel:{ color: "#6b7280", fontSize: 13 },
  statsValue:{ color: "#111827", fontSize: 13, fontWeight: 600 },

  geoSection: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 24 },
  geoCard:    { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" },
  geoTitle:   { fontSize: 13, fontWeight: 700, color: "#0f172a", padding: "16px 20px", borderBottom: "1px solid #f1f5f9" },
  geoEmpty:   { color: "#9ca3af", fontSize: 13, padding: "20px", textAlign: "center" },
  geoTable:   { width: "100%", borderCollapse: "collapse" },
  geoTh:      { padding: "9px 16px", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", textAlign: "left" },
  geoTd:      { padding: "10px 16px", fontSize: 13, color: "#374151", borderBottom: "1px solid #f9fafb" },
};
